import React, { useState, useEffect, useCallback } from 'react';
import Button from '~/app/common/Button';
import classes from './style.module.scss';

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 9999999;

export interface QuantityProps {
  quantity: number
  quantityChange: (value: number) => void
}

export type QuantityComponent = React.FC<QuantityProps>

const clamp = (num: number) => Math.min(Math.max(num, MIN_QUANTITY), MAX_QUANTITY);

const Quantity: QuantityComponent = ({ quantity, quantityChange }) => {
  const [draft, setDraft] = useState(quantity.toString());

  useEffect(() => {
    setDraft(quantity.toString());
  }, [quantity]);

  const handleQuantityChange = useCallback((delta: number) => {
    const qty = clamp(quantity + delta);
    quantityChange(qty);
  }, [quantity, quantityChange]);

  const handleInputChange = useCallback((value: string) => {
    const digitsOnly = value.replace(/[^0-9]/g, '');

    const trimmed = digitsOnly.length > MAX_QUANTITY.toString().length
      ? digitsOnly.slice(0, MAX_QUANTITY.toString().length)
      : digitsOnly;

    setDraft(trimmed);

    if (trimmed === '') return;

    const num = parseInt(trimmed, 10);
    quantityChange(clamp(num));
  }, [quantityChange]);

  const handleBlur = useCallback(() => {
    const num = parseInt(draft, 10);
    if (draft === '' || isNaN(num)) {
      setDraft(MIN_QUANTITY.toString());
      quantityChange(MIN_QUANTITY);
      return;
    }
    const clamped = clamp(num);
    setDraft(clamped.toString());
    if (clamped !== num) {
      quantityChange(clamped);
    }
  }, [draft, quantityChange]);

  return (
    <div className={classes['quantityControls']}>
      <Button onClick={() => handleQuantityChange(-1)} disabled={quantity <= MIN_QUANTITY}>-</Button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        onChange={e => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        className={classes['quantityInput']}
        style={{ minWidth: (draft.length + 1) + 'ch' }}
      />
      <Button onClick={() => handleQuantityChange(1)} disabled={quantity >= MAX_QUANTITY}>+</Button>
    </div>
  );
}

export default Quantity;
